import { General } from "./general.js";
import { Helper, BrowserStorageType } from "../app/core/util/helper.js";
import { Sheet } from "./sheet.js";
import { Config } from "../app/core/config/config.js";
import { StringKeyDictionary } from "../app/commonInterfaces.js";


export interface StateSummary {
    fromHSM: boolean;
    time: number;
    sourceSheetId: string;
    followSheetId: string;
    id: string;
    url: string;
    active: boolean;
    restoring: boolean;
    sheetIsClosing: boolean;
}

interface StateNode {
    state: StateSummary;
    prev: StateSummary;
    next: StateSummary;
}

interface StorageStateStack {
    state: StateSummary;
    prev: string;
    next: string;
}


const stack: Array<StateNode> = [];
const storageId = "__historyStack";
let initialized = false;


const createSummary = (url: string = null): StateSummary => {
    return {
        fromHSM: true,
        id: kendo.guid(),
        time: new Date().getTime(),
        followSheetId: null,
        sourceSheetId: null,
        url,
        active: false,
        restoring: false,
        sheetIsClosing: false
    };
}

const getStackNode = (id: string): StateNode => {
    if (Helper.hasValue(id)) {
        for (let i = 0; i < stack.length; i++) {
            if (stack[i].state.id === id) {
                return stack[i];
            }
        }
    }

    return null;
}

const getCurrentStateNode = () => {
    const state: StateSummary = history.state;
    if (state && state.fromHSM === true) {
        return getStackNode(state.id);
    }

    return null;
}

const getActiveStackNode = () => {
    for (let i = 0; i < stack.length; i++) {
        if (stack[i].state.active === true) {
            return stack[i];
        }
    }

    return null;
}

const loadStackFromStorage = () => {
    const storageData: Array<StorageStateStack> = Helper.loadDataFromStorage(storageId, BrowserStorageType.Session);
    const nextIds: StringKeyDictionary<string> = {};
    const prevIds: StringKeyDictionary<string> = {};

    stack.length = 0;
    if (Array.isArray(storageData)) {
        storageData.forEach(state => {
            stack.push({
                state: state.state,
                next: null,
                prev: null
            });
            nextIds[state.state.id] = state.next;
            prevIds[state.state.id] = state.prev;

        });

        stack.forEach(state => {
            const next = getStackNode(nextIds[state.state.id]);
            const prev = getStackNode(prevIds[state.state.id]);
            state.next = next ? next.state : null;
            state.prev = prev ? prev.state : null;
        });
    }
}

const saveStackToStorage = () => {
    const storageStack: Array<StorageStateStack> = stack.map(state => {
        return {
            state: state.state,
            next: Helper.hasValue(state.next) ? state.next.id : null,
            prev: Helper.hasValue(state.prev) ? state.prev.id : null
        };
    });

    Helper.saveDataToStorage(storageId, storageStack, false, BrowserStorageType.Session);
}

const pushToStack = (state: StateSummary) => {
    const newState: StateNode = {
        state,
        prev: null,
        next: null
    };

    if (stack.length > 0) {
        const current = getActiveStackNode();
        if (current) {
            newState.prev = current.state;
            current.next = newState.state;
        }
    }

    stack.push(newState);

    pruneStack();

    return newState;
}

const pruneStack = () => {
    const toDelete: Array<number> = [];
    //ignore first and last
    for (let i = 1; i < stack.length - 1; i++) {
        const summary = stack[i];
        let shouldDelete = true;

        if (summary.next) {
            const nextSummary = getStackNode(summary.next.id);
            if (nextSummary && nextSummary.prev === summary.state) {
                shouldDelete = false;
            }
        }

        if (summary.prev) {
            const prevSummary = getStackNode(summary.prev.id);
            if (prevSummary && prevSummary.next === summary.state) {
                shouldDelete = false;
            }
        }

        if (shouldDelete) {
            toDelete.push(i);
        }
    }

    for (let i = toDelete.length - 1; i >= 0; i--) {
        stack.splice(toDelete[i], 1);
    }
}

const setSummaryAsActive = (summary: StateSummary) => {
    stack.forEach(stateStack => {
        if (stateStack.state === summary) {
            stateStack.state.active = true;
        } else {
            stateStack.state.active = false;
        }
    });
}

const sanitizeURL = (url: string): string => {
    if (Helper.hasValue(url)) {
        const lowerCaseUrl = url.toLowerCase();
        if (lowerCaseUrl.indexOf('http://') !== 0 && lowerCaseUrl.indexOf('https://') !== 0) {
            if (lowerCaseUrl.indexOf(Config.baseUrl.toLowerCase()) !== 0) {
                url = Config.baseUrl + url;
            }
        }
        url = url.replace(/&isWindow=true/gi, "").replace(/isWindow=true/gi, "");
    }

    return url;
}

const handlePopState = () => {
    const state: StateSummary = history.state;

    //only handle states create with this manager
    if (Helper.hasValue(state) && state.fromHSM === true) {
        const currentState = getStackNode(state.id);
        const previousState = getActiveStackNode();
        let navigatingBack = currentState.state.time < previousState.state.time;

        //get the sheet associated with the current state/URL
        const stateSheet = General.Sheet.Manager.getSheetById(currentState.state.sourceSheetId);
        //get the sheet that was oppened from the current state/URL and pushed a state
        const followSheet = General.Sheet.Manager.getSheetById(currentState.state.followSheetId);

        //update the active flag
        if (currentState.state.active !== true) {
            setSummaryAsActive(currentState.state);
            saveStackToStorage();
        }

        //LABEL 1
        //handle "restoring" states
        if (currentState.state.restoring === true) {
            //clear the flag
            currentState.state.restoring = false;
            saveStackToStorage();
            //the action must be reverted
            navigatingBack = !navigatingBack;

            //the sheet was not yet closed
            if (stateSheet) {
                //the last sheet to close is per default the current state sheet
                let lastSheetToClose = currentState.state.sourceSheetId;
                let shouldNavigate = false;

                //if the current sheet is the "main" (first) sheet...
                if (General.Sheet.Manager.getSheetByIndex(0) === stateSheet) {
                    //...check if there are any other sheets open
                    const nextSheet = General.Sheet.Manager.getSheetByIndex(1);
                    if (nextSheet) {
                        //the "main" sheet should not be closed, because the page will be empty
                        //close all until the next sheet...
                        lastSheetToClose = nextSheet.id();
                        //...and then trigger a page navigation
                        shouldNavigate = true;
                    } else {
                        //the "main" sheet is the only one shown
                        const unloadHelper = stateSheet.getUnloadHelper();
                        //the "restoring" state is triggered when changes have not been saved
                        //show the "discard changes" message and if the user does not want to save,
                        //navigate to the "original" state (the state which triggered the "restoring" state)
                        unloadHelper.showUnloadMessage().then(okPressed => {
                            if (okPressed) {
                                unloadHelper.markEntitySaved();
                                //navigation is reverted!
                                if (navigatingBack) {
                                    history.back();
                                } else {
                                    history.forward();
                                }
                            }
                        });
                        return;
                    }
                }

                //trigger close on all of the following sheets
                General.Sheet.Manager.cascadeRemoveSheets(lastSheetToClose, () => {
                    if (shouldNavigate) {
                        if (navigatingBack) {
                            history.back();
                        } else {
                            history.forward();
                        }
                    }
                });
            }

            return;
        }

        if (currentState.state.sheetIsClosing === true) {
            currentState.state.sheetIsClosing = false;
            saveStackToStorage();
        }

        if (previousState.state.sheetIsClosing === true) {
            return;
        }

        //get the list of sheets that have unsaved changes
        const sheetsWithChanges = General.Sheet.Manager.getAllSheetsWithChanges();
        if (sheetsWithChanges.length > 0) {
            const idxSource = General.Sheet.Manager.getSheetIndex(previousState.state.sourceSheetId);
            //check for sheets with unsaved changes which are "on top" of the sheet associated with the URL
            if (idxSource !== - 1 && sheetsWithChanges.some(x => x.index >= idxSource)) {
                //there is at least one sheet with unsaved changes, starting with the sheet associated with the state
                //mark the previous state as "restoring"
                previousState.state.restoring = true;
                saveStackToStorage();

                //trigger the previous state => see LABEL 1
                if (navigatingBack) {
                    history.forward();
                } else {
                    history.back();
                }

                return;
            }
        }

        //there aren't any unsaved changes which affect the current state/URL
        if (navigatingBack) {
            //if the current (popped) state has opened a sheet (associated with the previous state)
            if (currentState.state.followSheetId && previousState.state.sourceSheetId === currentState.state.followSheetId) {

                //remove the sheet reference (either "back" or "sheet.hide()")
                currentState.state.followSheetId = null;
                //... "replaceState" also save the change in the "currentState" object
                StateManger.replaceState(currentState.state.url);

                //the sheet === null when the user already closed it
                if (followSheet && !followSheet.isRemoving()) {
                    General.Sheet.Manager.cascadeRemoveSheets(followSheet.id());
                }

                return;
            }
            //if not, let it go further and reload the page
        }

        //if we got this far, the URL has been changed and the page must be dynamically loaded
        location.reload();
    }
}


export const StateManger = {
    pushSheetState: (sheet: Sheet, url: string) => {
        const sheetId = sheet.id();
        const sanitizedUrl = sanitizeURL(url);
        const summary = createSummary(sanitizedUrl);
        summary.sourceSheetId = sheetId;
        //copy the reference so that it can be accessed inside the sheet close callback
        let currentSummary = getActiveStackNode();
        currentSummary.state.followSheetId = sheetId;

        sheet.setUpAfterCloseForPopState(() => {
            //should be called only one time
            sheet.setUpAfterCloseForPopState(null);
            //signal that the sheet is closing => we jest need to go back, don't trigger a new "close"
            summary.sheetIsClosing = true;
            saveStackToStorage();
            //sheet closed by the user
            if (currentSummary.state.followSheetId === sheetId) {
                //go back to the state which pushed
                history.back();
            }
        });

        pushToStack(summary);
        setSummaryAsActive(summary);
        saveStackToStorage();
        history.pushState(summary, "", sanitizedUrl);

        return summary.id;
    },
    pushState: (url: string) => {
        const sanitizedUrl = sanitizeURL(url);
        const summary = createSummary(sanitizedUrl);

        pushToStack(summary);
        setSummaryAsActive(summary);
        saveStackToStorage();
        history.pushState(summary, "", sanitizedUrl);

        return summary.id;
    },
    replaceState: (url: string) => {
        let stateNode: StateNode = null;
        const sanitizedUrl = sanitizeURL(url);
        const state: StateSummary = history.state;

        if (Helper.hasValue(state) && state.fromHSM === true) {
            stateNode = getStackNode(state.id);
            if (stateNode) {
                stateNode.state.url = sanitizedUrl;
            }
        }
        if (!stateNode) {
            const summary: StateSummary = createSummary(sanitizedUrl);
            stateNode = pushToStack(summary);
        }

        setSummaryAsActive(stateNode.state);
        saveStackToStorage();
        history.replaceState(stateNode.state, "", sanitizedUrl);

        return stateNode.state.id;
    },
    acquireState: () => {
        return StateManger.replaceState(window.location.href);
    },
    getCurrentStateSummary: (): StateSummary => {
        return JSON.parse(JSON.stringify(getActiveStackNode().state));
    },
    init: () => {
        if (!initialized) {
            loadStackFromStorage();
            window.addEventListener("popstate", handlePopState);
            StateManger.acquireState();
            const intervalHandle = setInterval(() => {
                const sheet = General.Sheet.Manager.getTopMostSheet();
                if (sheet) {
                    clearInterval(intervalHandle);
                    const currentSummary = getActiveStackNode();
                    //after a reload, there should be only one sheet
                    currentSummary.state.sourceSheetId = sheet.id();
                    saveStackToStorage();
                }
            }, 10);

            initialized = true;
        }
    }
}
